import { create } from 'zustand';

import type { ThemePreference } from '../api/types';

import { accentForeground } from './contrast';

export type ResolvedTheme = 'light' | 'dark';

/** Keys are read by the boot script (public/boot.js, loaded from index.html) to avoid a flash of the wrong theme. */
export const THEME_STORAGE_KEY = 'orbita.theme';
export const ACCENT_STORAGE_KEY = 'orbita.accent';
/** Text color on the stored accent (computed once; the boot script applies it before the first paint). */
export const ACCENT_FOREGROUND_STORAGE_KEY = 'orbita.accent-foreground';

const DARK_QUERY = '(prefers-color-scheme: dark)';

/** Color of the browser's (and the installed app's) title bar per theme: the surface color; public/boot.js repeats it. */
export const THEME_COLORS: Record<ResolvedTheme, string> = { light: '#f7f8fa', dark: '#16171b' };

/** Sets every `<meta name="theme-color">` to the resolved theme's color (they differ only by the system's color scheme). */
export function applyThemeColorMeta(resolved: ResolvedTheme, doc: Document = document): void {
  for (const meta of doc.querySelectorAll('meta[name="theme-color"]')) {
    meta.setAttribute('content', THEME_COLORS[resolved]);
  }
}

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Storage unavailable: preference applies to this session only.
  }
}

export const isThemePreference = (value: unknown): value is ThemePreference =>
  value === 'light' || value === 'dark' || value === 'system';

const systemPrefersDark = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(DARK_QUERY).matches;

export const resolveTheme = (theme: ThemePreference, prefersDark = systemPrefersDark()): ResolvedTheme =>
  theme === 'system' ? (prefersDark ? 'dark' : 'light') : theme;

/** A CSS color accepted for the accent (hex, rgb(), hsl(), oklch()…). */
export function isValidCssColor(value: string): boolean {
  if (typeof CSS === 'undefined' || typeof CSS.supports !== 'function')
    return /^#[0-9a-f]{3,8}$/iu.test(value);
  return CSS.supports('color', value);
}

interface ThemeState {
  theme: ThemePreference;
  resolved: ResolvedTheme;
  /** Runtime accent color (overrides the `--brand` token); null = default indigo. */
  accent: string | null;
  setTheme: (theme: ThemePreference) => void;
  setAccent: (accent: string | null) => void;
  /** Re-evaluates `system` after the OS theme changed. */
  syncSystem: () => void;
}

const initialTheme: ThemePreference = (() => {
  const stored = read(THEME_STORAGE_KEY);
  return isThemePreference(stored) ? stored : 'system';
})();

export const useThemeStore = create<ThemeState>()((set, get) => ({
  theme: initialTheme,
  resolved: resolveTheme(initialTheme),
  accent: read(ACCENT_STORAGE_KEY),
  setTheme: (theme) => {
    write(THEME_STORAGE_KEY, theme);
    set({ theme, resolved: resolveTheme(theme) });
  },
  setAccent: (accent) => {
    const value = accent && isValidCssColor(accent) ? accent : null;
    write(ACCENT_STORAGE_KEY, value);
    write(ACCENT_FOREGROUND_STORAGE_KEY, value ? accentForeground(value) : null);
    set({ accent: value });
  },
  syncSystem: () => {
    const resolved = resolveTheme(get().theme);
    if (resolved !== get().resolved) set({ resolved });
  },
}));

/** Applies the resolved theme class and accent variable to <html>; returns a cleanup function. */
export function startThemeController(): () => void {
  const root = document.documentElement;
  const apply = (state: Pick<ThemeState, 'resolved' | 'accent'>) => {
    // Light is the default token set; only `.dark` is toggled (`.light` is reserved for nested preview scopes).
    root.classList.toggle('dark', state.resolved === 'dark');
    root.classList.remove('light');
    root.style.colorScheme = state.resolved;
    applyThemeColorMeta(state.resolved);
    if (state.accent) {
      root.style.setProperty('--brand', state.accent);
      root.style.setProperty('--brand-foreground', accentForeground(state.accent));
    } else {
      root.style.removeProperty('--brand');
      root.style.removeProperty('--brand-foreground');
    }
  };
  apply(useThemeStore.getState());
  const unsubscribe = useThemeStore.subscribe((state, prev) => {
    if (state.resolved !== prev.resolved || state.accent !== prev.accent) apply(state);
  });

  const mql = typeof window.matchMedia === 'function' ? window.matchMedia(DARK_QUERY) : null;
  const onSystemChange = () => useThemeStore.getState().syncSystem();
  mql?.addEventListener('change', onSystemChange);
  return () => {
    unsubscribe();
    mql?.removeEventListener('change', onSystemChange);
  };
}

export const useResolvedTheme = (): ResolvedTheme => useThemeStore((s) => s.resolved);
